import type { OpfsWriterRequest, OpfsWriterResponse } from './opfs-writer.worker';
import { fileExtension } from './support';

const RECORDINGS_DIRECTORY = 'local-recordings';
const META_PREFIX = 'meet.localRecording.';
const SAVED_CLEANUP_DELAY_MS = 5 * 60 * 1000;

export type RecordingMeta = { id: string; roomName: string; mimeType: string; startedAt: number; saved?: boolean };

export type RecordingSink = {
    readonly kind: 'opfs' | 'memory';
    write: (chunk: Blob) => void;
    finish: () => Promise<Blob>;
};

export type UnsavedRecording = { meta: RecordingMeta; file: File };

type RequestPayload = OpfsWriterRequest extends infer Request ? (Request extends unknown ? Omit<Request, 'id'> : never) : never;

const lockName = (id: string) => `meet-local-recording:${id}`;
const storedFileName = (meta: Pick<RecordingMeta, 'id' | 'mimeType'>) => `${meta.id}.${fileExtension(meta.mimeType)}`;

function readMeta(id: string): RecordingMeta | undefined {
    const raw = localStorage.getItem(META_PREFIX + id);
    if (!raw) return undefined;
    try {
        return JSON.parse(raw) as RecordingMeta;
    } catch {
        return undefined;
    }
}

function writeMeta(meta: RecordingMeta) {
    localStorage.setItem(META_PREFIX + meta.id, JSON.stringify(meta));
}

function opfsSupported() {
    return typeof Worker === 'function' && 'storage' in navigator && typeof navigator.storage.getDirectory === 'function';
}

async function recordingsDirectory() {
    const root = await navigator.storage.getDirectory();
    return root.getDirectoryHandle(RECORDINGS_DIRECTORY, { create: true });
}

async function openOpfsSink(meta: RecordingMeta, onError: (error: Error) => void): Promise<RecordingSink> {
    const worker = new Worker(new URL('opfs-writer.worker.ts', import.meta.url), { type: 'module' });
    const pending = new Map<number, { resolve: () => void; reject: (error: Error) => void }>();
    let nextId = 0;
    worker.addEventListener('message', (event: MessageEvent<OpfsWriterResponse>) => {
        const request = pending.get(event.data.id);
        pending.delete(event.data.id);
        if (event.data.error === undefined) request?.resolve();
        else request?.reject(new Error(event.data.error));
    });
    worker.addEventListener('error', (event) => {
        onError(new Error(event.message || 'OPFS worker failed'));
    });
    const send = (payload: RequestPayload) =>
        new Promise<void>((resolve, reject) => {
            const id = nextId++;
            pending.set(id, { resolve, reject });
            worker.postMessage({ ...payload, id });
        });

    const fileName = storedFileName(meta);
    try {
        await send({ type: 'open', directory: RECORDINGS_DIRECTORY, fileName });
    } catch (error) {
        worker.terminate();
        throw error;
    }

    return {
        kind: 'opfs',
        write: (chunk) => {
            send({ type: 'write', chunk }).catch(onError);
        },
        finish: async () => {
            await send({ type: 'close' });
            worker.terminate();
            const directory = await recordingsDirectory();
            const handle = await directory.getFileHandle(fileName);
            return handle.getFile();
        },
    };
}

function openMemorySink(meta: RecordingMeta): RecordingSink {
    const chunks: Blob[] = [];
    return {
        kind: 'memory',
        write: (chunk) => {
            chunks.push(chunk);
        },
        finish: () => Promise.resolve(new Blob(chunks, { type: meta.mimeType })),
    };
}

export async function openRecordingSink(meta: RecordingMeta, onError: (error: Error) => void): Promise<RecordingSink> {
    if (opfsSupported()) {
        try {
            const sink = await openOpfsSink(meta, onError);
            writeMeta(meta);
            return sink;
        } catch (error) {
            console.warn('Local recording falls back to memory', error);
        }
    }
    return openMemorySink(meta);
}

export function holdRecordingLock(id: string): () => void {
    if (!('locks' in navigator)) return () => undefined;
    let release: (() => void) | undefined;
    const held = new Promise<void>((resolve) => {
        release = resolve;
    });
    void navigator.locks.request(lockName(id), () => held);
    return () => {
        release?.();
    };
}

export async function forgetRecording(meta: Pick<RecordingMeta, 'id' | 'mimeType'>) {
    localStorage.removeItem(META_PREFIX + meta.id);
    if (!opfsSupported()) return;
    const directory = await recordingsDirectory();
    await directory.removeEntry(storedFileName(meta)).catch(() => undefined);
}

export function markRecordingSaved(meta: RecordingMeta) {
    writeMeta({ ...meta, saved: true });
    setTimeout(() => {
        void forgetRecording(meta);
    }, SAVED_CLEANUP_DELAY_MS);
}

type IterableDirectory = FileSystemDirectoryHandle & { entries: () => AsyncIterable<[string, FileSystemHandle]> };

export async function listUnsavedRecordings(): Promise<UnsavedRecording[]> {
    if (!opfsSupported()) return [];
    const directory = (await recordingsDirectory()) as IterableDirectory;
    const lockSnapshot = 'locks' in navigator ? await navigator.locks.query() : undefined;
    const heldLocks = new Set((lockSnapshot?.held ?? []).map((lock) => lock.name));
    const unsaved: UnsavedRecording[] = [];
    for await (const [name, handle] of directory.entries()) {
        if (handle.kind !== 'file') continue;
        const id = name.replace(/\.[^.]+$/, '');
        if (heldLocks.has(lockName(id))) continue;
        const file = await (handle as FileSystemFileHandle).getFile();
        const meta = readMeta(id) ?? {
            id,
            roomName: '',
            mimeType: name.endsWith('.mp4') ? 'video/mp4' : 'video/webm',
            startedAt: file.lastModified,
        };
        if (meta.saved || file.size === 0) {
            await forgetRecording(meta);
            continue;
        }
        unsaved.push({ meta, file });
    }
    return unsaved;
}

function pad(value: number) {
    return String(value).padStart(2, '0');
}

export function recordingFileName(meta: Pick<RecordingMeta, 'roomName' | 'mimeType' | 'startedAt'>): string {
    const date = new Date(meta.startedAt);
    const day = `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const time = `${pad(date.getHours())}-${pad(date.getMinutes())}`;
    const room = meta.roomName.replaceAll(/[\\/:*?"<>|]/g, '_').trim();
    return [room, day, time].filter(Boolean).join(' ') + `.${fileExtension(meta.mimeType)}`;
}

export function downloadRecording(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, SAVED_CLEANUP_DELAY_MS);
}
